import supportModule from '../supportModule';

const sierraC3ChatEligibilitySuccessMessage = 'ChatSupportEligibilityCheck_Success';

function supportFormController(
  $log,
  $q,
  $window,
  $document,
  $scope,
  supportService,
  modalService,
  languageResource,
  regexService,
  captchaV2Constants
) {
  'ngInject';

  function normalizeUsername(username) {
    // Undefined local for lowercase is most neutral to all cultures similar to DotNet's ToLowerInvariantCulture
    // Normalize NFC resolves issues with characters that have multiple valid representations in Unicode
    return username?.toLocaleLowerCase('und')?.normalize('NFC');
  }

  function resetForm() {
    $scope.supportTicket = {};
    $scope.$on('onPageMetaDataInit', function () {
      $scope.supportTicket.username = $scope.accountContent.userInfo.data.Name;
    });
    $scope.supportTicket.name = '';
  }

  // Safely apply scope changes to avoid Angular 'locking' current view on digest cycle
  function safeApply(fn) {
    if ($scope.$root.$$phase === '$apply' || $scope.$root.$$phase === '$digest') {
      fn();
    } else {
      $scope.$apply(fn);
    }
  }

  function resetToPristinePostSubmission(form) {
    safeApply(function () {
      form.$setPristine();
      form.$setSubmitted();
      resetForm();
      $scope.formSubmitting = false;
      try {
        $window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
        $document.body.scrollTop = 0; // For Safari
        $document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE, and Opera
      } catch (e) {
        if (!$scope.isProd) console.error('Error auto-scrolling to top of page', e);
      }
    });
  }

  function launchChatAfterEligibilityCheck(form) {
    const partialScope = {
      accountContent: $scope.accountContent,
      supportTicket: $scope.supportTicket
    };
    resetToPristinePostSubmission(form);
    $scope.$emit('onChatEligibilityTrue', partialScope);
  }

  function showSupportFormSubmissionSuccessModal(form) {
    resetToPristinePostSubmission(form);
    modalService.open({
      titleText: languageResource.get('Heading.Dialog.RequestReceived'),
      bodyText: languageResource.get('Response.Dialog.RequestReceived'),
      neutralButtonText: languageResource.get('Action.Dialog.OK')
    });
  }

  function isNullOrWhiteSpace(input) {
    return !input || !input.trim();
  }

  function startSupportTicketRequest(form, captchaData) {
    const ticket = $scope.supportTicket;
    const supportTicket = {
      name: isNullOrWhiteSpace(ticket.name) ? 'Guest' : ticket.name,
      email: ticket.email,
      emailConfirm: ticket.emailConfirm,
      username: normalizeUsername(ticket.username),
      message: ticket.message,
      mainCategory: ticket.mainCategory.mainCategory.name,
      subCategory: ticket.subCategory,
      deviceType: ticket.deviceType,
      ageCategory: $scope.userAge13AndOver ? 'Age13AndOver' : 'AgeUnder13',
      assetID: ticket.assetID,
      universeID: ticket.universeID
    };

    // this adds captcha token and captcha provider fields to the request
    // if captchaData is defined (it's safe if captchaData is undefined or null)
    Object.assign(supportTicket, captchaData);

    supportService.submitForm(supportTicket, $scope.pageMetaData.submitFormUrl).then(
      function (result) {
        if (result.success) {
          // If the user is eligible for chat, launch chat and bypass direct support ticket submission
          // Otherwise show support form submission success modal (default ticket submission behavior)
          const isChatbotEligible =
            String(result.message).trim() === sierraC3ChatEligibilitySuccessMessage;

          if (isChatbotEligible) launchChatAfterEligibilityCheck(form);
          else showSupportFormSubmissionSuccessModal(form);
          return $q.resolve();
        }

        // If result is not successful, handle captcha flow if error returned relates to captcha validation
        let dxBlob;
        let captchaId = '';
        if (result.ErrorCode === 'CAPTCHA') {
          if (result.FieldData != null) {
            try {
              captchaData = JSON.parse(result.FieldData);
              dxBlob = captchaData.dxBlob;
              captchaId = captchaData.unifiedCaptchaId;
            } catch (e) {
              // backward compatible with old version that returns string only.
              dxBlob = result.FieldData;
            }
          }
          $scope.captchaInputParams = { unifiedCaptchaId: captchaId, dataExchange: dxBlob };
          $scope.captchaActivated = true;
          $scope.formSubmitting = false;
          return $q.resolve();
        }

        // Otherwise by default show error modal for non-captcha errors
        modalService.open({
          titleText: languageResource.get('Heading.Dialog.ErrorWithoutContext'),
          bodyText: result.message,
          neutralButtonText: languageResource.get('Action.Dialog.OK')
        });
        $scope.formSubmitting = false;
      },
      function () {
        modalService.open({
          titleText: languageResource.get('Heading.Dialog.ErrorWithoutContext'),
          bodyText: languageResource.get('Response.Dialog.ErrorWithoutContext'),
          neutralButtonText: languageResource.get('Action.Dialog.OK')
        });
        $scope.formSubmitting = false;
      }
    );
  }

  function showInvalidUsernameModal(form, captchaData) {
    const modal = modalService.open({
      titleText: languageResource.get('Heading.Dialog.InvalidUsername'),
      bodyText: languageResource.get('Response.Dialog.InvalidUsername'),
      actionButtonShow: true,
      actionButtonText: languageResource.get('Action.Dialog.Send'),
      neutralButtonText: languageResource.get('Action.Dialog.Cancel')
    });
    modal.result
      .then(
        function () {
          return startSupportTicketRequest(form, captchaData);
        },
        function () {
          // User Editing form
          $scope.formSubmitting = false;
        }
      )
      .then(d => d); // To ensure we do not interrupt nested request (i.e. promise chain) early and lock view
  }

  $scope.emailError = function () {
    return (
      !$scope.emailFocus &&
      $scope.supportForm.email.$dirty &&
      ($scope.supportForm.email.$error.required || $scope.supportForm.email.$error.pattern)
    );
  };

  $scope.emailsMatch = function () {
    return $scope.supportTicket.email === $scope.supportTicket.emailConfirm;
  };

  $scope.emailConfirmError = function () {
    return (
      !$scope.emailConfirmFocus && $scope.supportForm.emailConfirm.$dirty && !$scope.emailsMatch()
    );
  };

  $scope.assetIDError = function () {
    return (
      !$scope.assetIDFocus &&
      $scope.supportForm.assetID.$dirty &&
      $scope.supportForm.assetID.$error.pattern
    );
  };

  $scope.canDisplayAssetIDField = function () {
    return $scope.supportTicket.subCategory === 'AppealContent';
  };

  $scope.universeIDError = function () {
    return (
      !$scope.universeIDFocus &&
      $scope.supportForm.universeID.$dirty &&
      $scope.supportForm.universeID.$error.pattern
    );
  };

  $scope.canDisplayUniverseIDField = function () {
    return $scope.supportTicket.subCategory === 'AppealExperienceGenre';
  };

  $scope.submit = function (form, captchaData) {
    $scope.formSubmitting = true;

    // Avoid form submission if non-regex validation rules fail
    if (typeof $scope.supportTicket.username === 'undefined') {
      showInvalidUsernameModal(form, captchaData);
      return;
    }

    if ($scope.emailConfirmError()) {
      $scope.formSubmitting = false;
      return;
    }

    // Otherwise verify username async and submit form
    supportService
      .checkValidUsername(normalizeUsername($scope.supportTicket.username))
      .then(
        function (response) {
          const isValidUsernameResponse =
            response.usernames &&
            normalizeUsername(response.usernames[0]) ===
              normalizeUsername($scope.supportTicket.username);

          if (!isValidUsernameResponse) {
            showInvalidUsernameModal(form, captchaData);
            return $q.resolve();
          }

          return startSupportTicketRequest(form, captchaData);
        },
        function () {
          $scope.usernameErrorMessage = '';
        }
      )
      .then(d => d); // To ensure we do not interrupt nested request (i.e. promise chain) early and lock view
  };

  $scope.captchaPassed = function (captchaData) {
    startSupportTicketRequest($scope.supportForm, captchaData);
  };

  $scope.captchaFailed = function () {
    modalService.open({
      titleText: languageResource.get('Heading.Dialog.ErrorWithoutContext'),
      bodyText: languageResource.get('Response.Dialog.ErrorWithoutContext'),
      neutralButtonText: languageResource.get('Action.Dialog.OK')
    });
  };

  $scope.init = function init() {
    resetForm();
    $scope.regexData = {};
    regexService.getEmailRegex().then(function (data) {
      if (data) $scope.regexData.email = data.Regex;
    });
    $scope.captchaActionTypes = captchaV2Constants.captchaActionTypes;
    $scope.captchaActivated = false;
    $scope.captchaReturnTokenInSuccessCb = true;
    $scope.isProd = !String($window.location.origin).includes('sitetest'); // ST1 should fully mirror PROD
    $scope.formSubmitting = false;
  };

  $scope.init();
}

supportModule.controller('supportFormController', supportFormController);

export default supportFormController;
